/**
 * Root SaaS application component.
 *
 * Simple hash-based routing:
 *   #/            -> Dashboard (project list)
 *   #/project/:id -> Project editor
 *   (no hash)     -> Login page (if unauthenticated) or Dashboard
 */

import {useEffect, useState, useCallback} from 'react';
import {useAuth} from './auth';
import {PageLoader} from './components';
import {LoginPage} from './pages/LoginPage';
import {Dashboard} from './pages/Dashboard';
import {ProjectEditor} from './pages/ProjectEditor';

type Route =
  | {page: 'dashboard'}
  | {page: 'project'; id: string};

function parseHash(): Route {
  const hash = window.location.hash.slice(1); // Remove leading #
  const projectMatch = hash.match(/^\/project\/([a-f0-9-]+)$/);
  if (projectMatch) {
    return {page: 'project', id: projectMatch[1]};
  }
  return {page: 'dashboard'};
}

export function SaasApp() {
  const {user, loading} = useAuth();
  const [route, setRoute] = useState<Route>(parseHash);

  const onHashChange = useCallback(() => {
    setRoute(parseHash());
  }, []);

  useEffect(() => {
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [onHashChange]);

  if (loading) {
    return <PageLoader />;
  }

  // Not authenticated -> login page
  if (!user) {
    return <LoginPage />;
  }

  // Authenticated -> route
  switch (route.page) {
    case 'project':
      return <ProjectEditor projectId={route.id} />;
    case 'dashboard':
    default:
      return <Dashboard />;
  }
}
